import { MapMouseEvent, MapRef } from 'react-map-gl';
import useMapActions from './MapActions';
import { RefObject, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MapConfigDto } from '../dtos';
import {
  setAreaLoaded,
  setCompoundViewBounds,
  setDrawerOpen,
  setMapAreas,
  setMapCompounds,
  setMapMode,
  setResultsType,
  setSearchDrawerOpen,
  setSelectedArea,
  setSelectedCompound,
  setViewBounds
} from '../../../redux/slices/mapSlice';
import { isEqual } from '../../../helpers/isEqual';
import { MapMode, ResultsType } from '../types';
import useMapSearch from './MapSearch';
import { useRouter } from 'next/router';
import { NAWY_NOW_ZOOM, SAHEL_CENTER, SAHEL_ZOOM } from '../config/mapConfig';
import {
  SearchContext,
  SearchContextInterface
} from '../../search-new/context/SearchContext';

const useMapControls = (mapRef: RefObject<MapRef>) => {
  const { asPath } = useRouter();
  const mapConfig: MapConfigDto = useSelector((state: any) => state.map.config);
  const viewBounds = useSelector((state: any) => state.map.viewBounds, isEqual);
  const { searchFiltersState } = useContext(
    SearchContext
  ) as SearchContextInterface;
  const compoundViewBounds = useSelector(
    (state: any) => state.map.compoundViewBounds,
    isEqual
  );
  const resultsType = useSelector(
    (state: any) => state.map.resultsType,
    isEqual
  );
  const mapMode = useSelector((state: any) => state.map.mapMode, isEqual);
  const dispatch = useDispatch();
  const { selectCompound, selectArea } = useMapActions();
  const { areas, compounds, isFetching } = useMapSearch();

  const selectedCompound = useSelector(
    (state: any) => state.map.selectedCompound
  );
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const selectedArea = useSelector((state: any) => state.map.selectedArea);

  const isNawyNowPage = asPath.includes('/nawy-now');

  const onMapLoad = () => {
    if (mapRef?.current && selectedCompound) {
      dispatch(setResultsType(ResultsType.COMPOUNDS));
      dispatch(setMapMode(MapMode.COMPOUND));
      dispatch(setDrawerOpen(true));
      dispatch(setAreaLoaded(true));
    } else if (mapRef?.current && selectedArea) {
      dispatch(setResultsType(ResultsType.COMPOUNDS));
      dispatch(setMapMode(MapMode.SEARCH));
      dispatch(setDrawerOpen(true));
      dispatch(setSearchDrawerOpen(true));
      dispatch(setAreaLoaded(true));
    } else if (mapRef?.current) {
      if (asPath.includes('sahel-map')) {
        dispatch(setResultsType(ResultsType.COMPOUNDS));
        dispatch(setAreaLoaded(true));
        mapRef.current.flyTo({
          center: SAHEL_CENTER,
          zoom: SAHEL_ZOOM,
          animate: false,
          essential: true
        });
      } else if (isNawyNowPage) {
        mapRef.current.flyTo({
          center: isMobile
            ? mapConfig?.mobile?.initialCenter
            : mapConfig?.desktop?.initialCenter,
          zoom: NAWY_NOW_ZOOM,
          animate: false,
          essential: true
        });
      } else {
        mapRef.current.flyTo({
          center: isMobile
            ? mapConfig?.mobile?.initialCenter
            : mapConfig?.desktop?.initialCenter,
          zoom: isMobile
            ? mapConfig?.mobile?.initialZoomLevel
            : mapConfig?.desktop?.initialZoomLevel,
          animate: true,
          essential: true,
          duration: 1000
        });
        dispatch(setAreaLoaded(true));
      }
    }
  };
  const recenterMap = () => {
    dispatch(setSelectedCompound(null));
    dispatch(setAreaLoaded(false));
    dispatch(setSelectedArea(null));
    dispatch(setCompoundViewBounds(null));
    dispatch(setViewBounds(null));
    if (!asPath.includes('sahel-map')) {
      dispatch(setResultsType(ResultsType.AREAS));
    }
    if (mapRef?.current) {
      if (asPath.includes('sahel-map')) {
        dispatch(setResultsType(ResultsType.COMPOUNDS));
        mapRef.current.flyTo({
          center: SAHEL_CENTER,
          zoom: SAHEL_ZOOM,
          animate: false,
          essential: true
        });
      } else if (isNawyNowPage) {
        mapRef.current.flyTo({
          center: isMobile
            ? mapConfig?.mobile?.initialCenter
            : mapConfig?.desktop?.initialCenter,
          zoom: NAWY_NOW_ZOOM,
          animate: false,
          essential: true
        });
      } else {
        mapRef.current.flyTo({
          center: isMobile
            ? mapConfig?.mobile?.initialCenter
            : mapConfig?.desktop?.initialCenter,
          zoom: isMobile
            ? mapConfig?.mobile?.initialZoomLevel
            : mapConfig?.desktop?.initialZoomLevel,
          animate: true,
          essential: true,
          duration: 500
        });
      }
    }
  };
  const onClick = (event: MapMouseEvent) => {
    const feature = event?.features ? event?.features[0] : null;
    if (feature) {
      const featureId = feature?.properties?.id;
      if (resultsType == ResultsType.AREAS) {
        selectArea(featureId);
      } else {
        selectCompound(featureId);
      }
    }
  };
  useEffect(() => {
    if (resultsType == ResultsType.AREAS && viewBounds && areas) {
      dispatch(setMapAreas(areas));
      dispatch(setMapCompounds([]));
    }
    if (
      resultsType == ResultsType.COMPOUNDS &&
      compoundViewBounds &&
      compounds
    ) {
      if (mapMode === MapMode.CalculateDistance) {
        const compoundsArr = compounds.filter((compound: any) => {
          return compound.northcoastKm || compound?.northcoast_km;
        });
        dispatch(setMapCompounds(compoundsArr));
        dispatch(setMapAreas([]));
      }
      // const uniqueCompounds = new Map();
      // [...allCompounds, ...compounds].forEach((compound: any) => {
      //   uniqueCompounds.set(compound.id, compound);
      // });

      // let uniqueCompoundsArray = Array.from(uniqueCompounds.values());
      // if (uniqueCompoundsArray.length > 100) {
      //   uniqueCompoundsArray = uniqueCompoundsArray.slice(-150);
      // }
      dispatch(setMapCompounds(compounds));
      dispatch(setMapAreas([]));
    }
  }, [areas, compounds]);

  useEffect(() => {
    if (
      searchFiltersState.searchFilters.areas.length > 1 &&
      mapRef?.current &&
      isNawyNowPage
    ) {
      recenterMap();
    }
  }, [searchFiltersState.searchFilters.areas]);
  useEffect(() => {
    if (
      searchFiltersState.searchFilters.compounds.length > 1 &&
      mapRef?.current &&
      isNawyNowPage
    ) {
      dispatch(setSelectedCompound(null));
    }
  }, [searchFiltersState.searchFilters.compounds]);

  return { onMapLoad, onClick, recenterMap, isFetching };
};

export default useMapControls;
